import { useState, type FormEvent } from 'react';
import { apiPatch } from '../lib/api';

// ---------------------------------------------------------------------------
// Types (mirroring engines-schema route)
// ---------------------------------------------------------------------------
export type FormField = {
  name: string;
  label: string;
  type: 'text' | 'number' | 'enum' | 'textarea';
  required: boolean;
  options?: string[];
  placeholder?: string;
};

export type FormSpec = { fields: FormField[] };

export type Product = {
  id: string;
  name: string;
  engine_slug: string;
  status: string;
  wrap_config: Record<string, unknown>;
};

type Props = {
  spec: FormSpec;
  product: Product;
  onSaved?: (updated: Product) => void;
  onCancel?: () => void;
};

// ---------------------------------------------------------------------------
// WrapForm
// ---------------------------------------------------------------------------
export function WrapForm({ spec, product, onSaved, onCancel }: Props) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of spec.fields) {
      const v = product.wrap_config[f.name];
      initial[f.name] = v !== undefined && v !== null ? String(v) : '';
    }
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(name: string, value: string) {
    setValues((prev) => ({ ...prev, [name]: value }));
    setSaved(false);
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    setError(null);

    // Validate required fields
    for (const f of spec.fields) {
      if (f.required && !values[f.name]?.trim()) {
        setError(`"${f.label}" is required.`);
        return;
      }
    }

    // Build the wrap payload, coercing number fields
    const wrap: Record<string, unknown> = {};
    for (const f of spec.fields) {
      const raw = values[f.name];
      if (raw === '' || raw === undefined) continue;
      if (f.type === 'number') {
        const n = Number(raw);
        if (!Number.isFinite(n)) {
          setError(`"${f.label}" must be a number.`);
          return;
        }
        wrap[f.name] = n;
      } else {
        wrap[f.name] = raw;
      }
    }

    setSaving(true);
    try {
      const result = await apiPatch<{ product: Product }>(
        `/api/products/${product.id}/wrap`,
        { wrap },
      );
      setSaved(true);
      onSaved?.(result.product);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      {spec.fields.map((field) => (
        <div key={field.name}>
          <label
            htmlFor={`wf-${field.name}`}
            className="block text-sm font-medium text-slate-300 mb-1"
          >
            {field.label}
            {field.required && <span className="ml-1 text-red-400">*</span>}
          </label>

          {field.type === 'textarea' && (
            <textarea
              id={`wf-${field.name}`}
              name={field.name}
              value={values[field.name] ?? ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
              className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-sky-500 focus:outline-none"
            />
          )}

          {field.type === 'enum' && field.options && (
            <select
              id={`wf-${field.name}`}
              name={field.name}
              value={values[field.name] ?? ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-sky-500 focus:outline-none"
            >
              <option value="">— select —</option>
              {field.options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          )}

          {(field.type === 'text' || field.type === 'number') && (
            <input
              id={`wf-${field.name}`}
              name={field.name}
              type={field.type === 'number' ? 'number' : 'text'}
              value={values[field.name] ?? ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-sky-500 focus:outline-none"
            />
          )}
        </div>
      ))}

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {saved && (
        <p role="status" className="text-sm text-emerald-400">
          Saved successfully.
        </p>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:border-slate-500"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
